/**
 * TagService - Direct API service for tag management
 * Calls backend /crs/tags endpoints directly
 */
import { apiClient } from 'src/careflow/ph-core/api_client/api_client';
import { ValueSet } from 'src/careflow/ph-core/fhir-client/resources/resources';

/**
 * Tag DTO for creating/updating tags
 */
export interface CreateTagDto {
  display: string;
  code: string;
  resources: string[];
  description?: string;
  comments?: string;
}

export interface UpdateTagDto {
  display?: string;
  code?: string;
  resources?: string[];
  description?: string;
  comments?: string;
  version?: string;
}

/**
 * TagService directly calls the backend API
 * Backend handles all FHIR operations (CodeSystem, ValueSet)
 */
export class TagService {
  private baseUrl = '/crs/tags';

  getTags = async (params?: {
    _id?: string;
    identifier?: string;
    search?: string;
  }): Promise<ValueSet[]> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?._id) queryParams.append('_id', params._id);
      if (params?.identifier) queryParams.append('identifier', params.identifier);
      if (params?.search) queryParams.append('_text', params.search);

      const queryString = queryParams.toString();
      const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
      
      const response = await apiClient.get(url);
      
      // Backend returns array of ValueSets
      if (Array.isArray(response)) {
        return response;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching tags:', error);
      return [];
    }
  }

  getTagById = async (id: string): Promise<ValueSet> => {
    const response: any = await apiClient.get(`${this.baseUrl}/${id}`);
    return response;
  }

  createTag = async (tag: CreateTagDto): Promise<ValueSet[]> => {
    try {
      const response: any = await apiClient.post(this.baseUrl, tag);
      return response;
    } catch (error: any) {
      console.error('Error creating tag:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  }

  updateTag = async (id: string, tag: UpdateTagDto): Promise<ValueSet> => {
    try {
      const response: any = await apiClient.put(`${this.baseUrl}/${id}`, tag);
      return response;
    } catch (error: any) {
      console.error('Error updating tag:', error);
      throw error;
    }
  }

  deleteTag = async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`${this.baseUrl}/${id}`);
    } catch (error: any) {
      console.error('Error deleting tag:', error);
      throw error;
    }
  }

  getTagResourceCounts = async (tagId: string): Promise<Record<string, number>> => {
    try {
      const response: any = await apiClient.get(`${this.baseUrl}/${tagId}/resource-counts`);
      return response;
    } catch (error) {
      console.error('Error fetching resource counts:', error);
      return {};
    }
  }
}

export const tagService = new TagService();

